import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Copy, Mail, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import NewsletterForm from "@/components/NewsletterForm";

const FLAG_KEY = "wanted_newsletter_popup";
const DISMISS_DAYS = 30;

// Popup iscrizione newsletter: solo visitatori non loggati, dopo qualche secondo.
// Chi si è loggato non lo rivede mai (nel profilo c'è la stessa proposta);
// chiudendolo con la X torna dopo 30 giorni, iscrivendosi non torna più.
export default function NewsletterPopup() {
  const { user } = useAuth(); // undefined = caricamento, null = ospite
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [done, setDone] = useState(null);
  const [offer, setOffer] = useState(null);

  useEffect(() => {
    if (user !== null) return; // ancora in caricamento o loggato: niente popup
    if (location.pathname.startsWith("/login") || location.pathname.startsWith("/admin")) return;
    const flag = localStorage.getItem(FLAG_KEY);
    if (flag === "subscribed") return;
    if (flag && Date.now() - Number(flag) < DISMISS_DAYS * 24 * 3600 * 1000) return;
    const t = setTimeout(() => {
      setOpen(true);
      api.get("/newsletter/offer").then((r) => setOffer(r.data)).catch(() => {});
    }, 7000);
    return () => clearTimeout(t);
  }, [user, location.pathname]);

  const dismiss = () => {
    try { localStorage.setItem(FLAG_KEY, String(Date.now())); } catch {}
    setOpen(false);
  };

  const handleSuccess = (data) => {
    try { localStorage.setItem(FLAG_KEY, "subscribed"); } catch {}
    setDone(data);
  };

  const closeAll = () => setOpen(false);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 40, scale: 0.96 }}
          transition={{ duration: 0.35, ease: "easeOut" }}
          data-testid="newsletter-popup"
          className="fixed bottom-4 left-4 z-40 w-[calc(100vw-2rem)] max-w-md slab-frame rounded-2xl p-6 shadow-2xl shadow-black/60 max-h-[85vh] overflow-y-auto"
        >
          <button
            onClick={dismiss}
            data-testid="newsletter-close-button"
            title="Chiudi"
            className="absolute top-4 right-4 text-zinc-500 hover:text-white transition-colors"
          >
            <X size={16} />
          </button>
          {!done ? (
            <>
              <div className="flex items-center gap-3 mb-2">
                <Mail size={18} className="text-red-500" />
                <h2 className="font-display text-lg font-bold uppercase tracking-tight text-white">Entra in Wanted</h2>
              </div>
              <p className="text-xs text-zinc-500 leading-relaxed mb-5">
                Iscriviti alla newsletter: scegli se seguire le carte, gli eSports o entrambi — ti avvisiamo solo su ciò che ti interessa.
                In regalo subito un <span className="text-red-400 font-bold">buono -{offer?.percent ?? 10}%</span> sul primo ordine.
              </p>
              {offer?.launch && (
                <p data-testid="newsletter-launch-badge" className="text-[10px] font-mono uppercase tracking-widest text-amber-400 border border-amber-500/40 rounded-full px-3 py-1.5 inline-block mb-5 -mt-2">
                  Offerta di lancio: ancora {offer.remaining} buoni -{offer.percent}%
                </p>
              )}
              <NewsletterForm onSuccess={handleSuccess} />
            </>
          ) : (
            <div data-testid="newsletter-success">
              <h2 className="font-display text-lg font-bold uppercase tracking-tight text-white mb-2">Benvenuto in Wanted!</h2>
              <p className="text-xs text-zinc-500 leading-relaxed mb-5">
                {done.already
                  ? "Eri già dei nostri: preferenze aggiornate. Il tuo buono è sempre questo:"
                  : done.bonus_repeat || !done.discount_code
                    ? "Bentornato! Preferenze aggiornate. Il buono di benvenuto si riceve una sola volta per email — ma le novità arrivano sempre."
                    : "Iscrizione confermata! Ecco il tuo buono di benvenuto, te lo abbiamo mandato anche via email:"}
              </p>
              {done.discount_code ? (
                <>
                  <div className="flex items-center gap-2 mb-5">
                    <code data-testid="newsletter-success-code" className="flex-1 bg-[#050505] border border-red-600/50 rounded-lg px-4 py-3.5 font-mono font-bold text-lg text-red-400 tracking-widest text-center">
                      {done.discount_code}
                    </code>
                    <button
                      data-testid="newsletter-copy-code-button"
                      onClick={() => { navigator.clipboard.writeText(done.discount_code); toast.success("Codice copiato!"); }}
                      title="Copia codice"
                      className="p-3.5 border border-white/15 hover:border-red-600 rounded-lg text-zinc-300 hover:text-white transition-colors"
                    >
                      <Copy size={15} />
                    </button>
                  </div>
                  <p className="text-[10px] text-zinc-600 mb-5">-{done.discount_percent}% sul primo ordine: inseriscilo al checkout entro 90 giorni.</p>
                </>
              ) : null}
              <button
                onClick={closeAll}
                data-testid="newsletter-done-button"
                className="w-full bg-red-600 hover:bg-red-700 text-white font-display font-bold uppercase tracking-wide text-sm px-6 py-3.5 rounded-full transition-all active:scale-[0.97]"
              >
                Inizia a esplorare
              </button>
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
